import { ASNFormData, NameEntry, ValidationError } from '../types/asn';
import type { TemplateConfig } from '../types/tradingPartner';

/**
 * Validate an ASN against the static required-field set, plus — when a
 * template config is provided — per-trading-partner name requirements.
 *
 * WDEV-53: names[] used to be ignored entirely. Missing Ship To rows were
 * slipping through as "Ready to Export" and breaking downstream mapping.
 * The validator now honours two new optional template declarations:
 *   - mandatoryFields.requiredNameTypes — entity types that must be present
 *   - mandatoryFields.namesByType       — per-entity-type required fields
 */
export function validateASN(data: ASNFormData, templateConfig?: TemplateConfig): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!data.purposeCode) {
    errors.push({ field: 'purposeCode', message: 'Purpose code is required', section: 'header' });
  }

  if (!data.shipmentIdentification) {
    errors.push({ field: 'shipmentIdentification', message: 'Shipment ID is required', section: 'header' });
  }

  if (!data.date || data.date.length !== 8) {
    errors.push({ field: 'date', message: 'Date must be in YYYYMMDD format', section: 'header' });
  }

  if (!data.time || data.time.length !== 4) {
    errors.push({ field: 'time', message: 'Time must be in HHMM format', section: 'header' });
  }

  if (!data.timeCode) {
    errors.push({ field: 'timeCode', message: 'Time code is required', section: 'header' });
  }

  if (!data.packagingCode) {
    errors.push({ field: 'packagingCode', message: 'Packaging code is required', section: 'header' });
  }

  if (!data.transportationMethod) {
    errors.push({ field: 'transportationMethod', message: 'Transportation method is required', section: 'header' });
  }

  if (!data.carrierIdentificationCode) {
    errors.push({ field: 'carrierIdentificationCode', message: 'Carrier code (SCAC) is required', section: 'header' });
  }

  if (!data.carrierReferenceNumber) {
    errors.push({ field: 'carrierReferenceNumber', message: 'Carrier reference number (PRO/BOL) is required', section: 'header' });
  }

  // Template-driven name rules (optional). Resolved once before iterating
  // orders so the checks apply consistently whether a template is passed or
  // not. Back-compat: when no template is provided, names[] is not checked
  // and behaviour matches pre-WDEV-53.
  const requiredNameTypes = templateConfig?.mandatoryFields?.requiredNameTypes ?? [];
  const namesByType = templateConfig?.mandatoryFields?.namesByType ?? {};

  if (!data.orders || data.orders.length === 0) {
    errors.push({ field: 'order', message: 'At least one order is required', section: 'orders' });
  }else{
    data.orders.forEach((order, orderIndex) => {
      if (!order.orderNumber) {
        errors.push({
          field: `order_${orderIndex}_orderNumber`,
          message: 'Order number is required',
          section: `order_${orderIndex}`,
        });
      }

      if (!order.orderDate || order.orderDate.length !== 8) {
        errors.push({
          field: `order_${orderIndex}_orderDate`,
          message: 'Order date must be in YYYYMMDD format',
          section: `order_${orderIndex}`,
        });
      }

      // WDEV-53: presence check — each entity type declared in the template's
      // requiredNameTypes must appear at least once in order.names[].
      const presentTypes = new Set<string>((order.names || []).map((n) => n.type));
      for (const requiredType of requiredNameTypes) {
        if (!presentTypes.has(requiredType)) {
          errors.push({
            field: `order_${orderIndex}_names_missing_${requiredType}`,
            message: `A name entry of type "${requiredType}" is required`,
            section: `order_${orderIndex}_names`,
          });
        }
      }

      // WDEV-53: per-entity-type field checks — for each name row, look up
      // its type in namesByType and surface one error per missing field.
      (order.names || []).forEach((name, nameIndex) => {
        const requiredForThisType = namesByType[name.type];
        if (!requiredForThisType || requiredForThisType.length === 0) return;
        for (const field of requiredForThisType) {
          const value = (name as unknown as Record<string, unknown>)[field];
          const isEmpty = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
          if (isEmpty) {
            errors.push({
              field: `order_${orderIndex}_name_${nameIndex}_${field}`,
              message: `${fieldLabel(field)} is required for ${name.type || 'name entry'}`,
              section: `order_${orderIndex}_name_${nameIndex}`,
            });
          }
        }
      });

      if (!order.details || order.details.length === 0) {
        errors.push({
          field: `order_${orderIndex}_details`,
          message: 'At least one line item is required',
          section: `order_${orderIndex}`,
        });
      }

      order.details.forEach((detail, detailIndex) => {
        if (!detail.line_number || detail.line_number < 1) {
          errors.push({
            field: `line_${orderIndex}_${detailIndex}_line_number`,
            message: 'Line number must be greater than 0',
            section: `order_${orderIndex}_line_${detailIndex}`,
          });
        }

        if (!detail.unitsShipped) {
          errors.push({
            field: `line_${orderIndex}_${detailIndex}_unitsShipped`,
            message: 'Units shipped is required',
            section: `order_${orderIndex}_line_${detailIndex}`,
          });
        }

        if (!detail.uom) {
          errors.push({
            field: `line_${orderIndex}_${detailIndex}_uom`,
            message: 'Unit of measure is required',
            section: `order_${orderIndex}_line_${detailIndex}`,
          });
        }

        if (!detail.customerOrderNumber) {
          errors.push({
            field: `line_${orderIndex}_${detailIndex}_customerOrderNumber`,
            message: 'Customer order number is required',
            section: `order_${orderIndex}_line_${detailIndex}`,
          });
        }

        if (!detail.orderAssignedId) {
          errors.push({
            field: `line_${orderIndex}_${detailIndex}_orderAssignedId`,
            message: 'Assigned ID is required',
            section: `order_${orderIndex}_line_${detailIndex}`,
          });
        }

        if (!detail.parts || detail.parts.length === 0) {
          errors.push({
            field: `line_${orderIndex}_${detailIndex}_parts`,
            message: 'At least one part is required',
            section: `order_${orderIndex}_line_${detailIndex}`,
          });
        }
      });
    });
  }
  return errors;
}

export function getErrorsAsRecord(errors: ValidationError[]): Record<string, string> {
  const record: Record<string, string> = {};
  errors.forEach((error) => {
    record[error.field] = error.message;
  });
  return record;
}

export function validateDateFormat(date: string): boolean {
  if (date.length !== 8) return false;
  if (!/^\d{8}$/.test(date)) return false; // Check if all characters are digits
  
  const year = parseInt(date.substring(0, 4));
  const month = parseInt(date.substring(4, 6));
  const day = parseInt(date.substring(6, 8));

  if (year < 1900 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  return true;
}

export function validateTimeFormat(time: string): boolean {
  if (time.length !== 4) return false;
  if (!/^\d{4}$/.test(time)) return false; // Check if all characters are digits

  const hours = parseInt(time.substring(0, 2));
  const minutes = parseInt(time.substring(2, 4));

  if (hours < 0 || hours > 23) return false;
  if (minutes < 0 || minutes > 59) return false;

  return true;
}

/**
 * Human-readable label for a NameEntry field name, used in template-driven
 * per-entity validation messages.
 */
function fieldLabel(field: string): string {
  const map: Record<string, string> = {
    id: 'ID',
    name: 'Name',
    qualifier: 'ID Type',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    postalCode: 'Zip',
    country: 'Country',
  };
  return map[field] ?? field;
}

// Re-export NameEntry so consumers can tree-shake without hopping to types/.
export type { NameEntry };
